import { CopyOutlined } from '@ant-design/icons'
import { Switch, Tag } from 'antd'
import { CreativeBean, CreativeListItemBean, ListResponseCreativeListItemBean } from 'common/api/api.ts'
import { creativeApi } from 'common/api/entitiesApi.tsx'
import { ActiveStatusSelector } from 'common/components/dictionary/ActiveStatusSelector.tsx'
import { CreativeStatusSelector } from 'common/components/dictionary/CreativeStatusSelector.tsx'
import { GoToReportButton } from 'common/components/GoToReportButton.tsx'
import { ListActionButton } from 'common/components/ListActionButton.tsx'
import { ListCard } from 'common/components/ListCard'
import { AdGroupSelector } from 'common/components/reloadable/AdGroupSelector.tsx'
import { AdvertiserSelector } from 'common/components/reloadable/AdvertiserSelector.tsx'
import { SearchInput } from 'common/components/SearchInput'
import { ListFilters, useAppContext } from 'common/context/AppContext.tsx'
import { CommonRoutes } from 'common/context/commonRoutes'
import { statusColors } from 'common/pages/creative/CreativeHelper.ts'
import { COLUMN_ID_WIDTH, COLUMN_NAME_WIDTH } from 'common/util/constants'
import { initListFilters, useListFilters } from 'common/util/useListFilters.ts'
import { apiCall, formatNames } from 'common/util/Util.tsx'
import dayjs from 'dayjs'
import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'


const CreativeList = ({agency}: { agency: boolean }) => {
    const [filters, setFilters] = useState<ListFilters>(initListFilters())
    const {setFilter} = useListFilters(setFilters)
    const navigateTo = useNavigate()
    const [listData, setListData] = useState<ListResponseCreativeListItemBean>()
    const [loading, setLoading] = useState(false)
    const appContext = useAppContext()

    const loadData = () => {
        apiCall(
            setLoading,
            appContext,
            () =>
                creativeApi.creativeList(
                    filters.sort?.name,
                    filters.sort?.direction,
                    filters.name,
                    filters.status,
                    filters.active,
                    filters.format,
                    filters.adGroupId,
                    filters.advertiserId,
                    undefined,
                    {query: {page: filters.page, size: filters.pageSize}}
                ),
            (res: ListResponseCreativeListItemBean) => setListData(res)
        )
    }

    const toggleActive = (record: CreativeBean, checked: boolean) => {
      if (record.id) {
        const id = record.id
        apiCall(
          setLoading,
          appContext,
          () => (checked ? creativeApi.massEnableCreative([id]) : creativeApi.massDisableCreative([id])),
          () => {
            record.active = checked
            setListData(listData)
          }
        )
      }
    }

    return (
        <ListCard
            loading={loading}
            title="Creatives"
            createName="Creative"
            createRoute={CommonRoutes.CREATIVE_CREATE}
            listRouteKey={CommonRoutes.CREATIVE}
            filters={filters}
            setFilters={setFilters}
            listData={listData}
            loadData={loadData}
            columns={[
                {
                    title: 'ID',
                    dataIndex: 'id',
                    width: COLUMN_ID_WIDTH,
                    fixed: 'left'
                },
                {
                    title: 'Name',
                    dataIndex: 'name',
                    className: 'list-table-name',
                    render: (text, record: CreativeListItemBean) => {
                        return (
                            <Link style={{color: '#5888CF'}} to={`${CommonRoutes.CREATIVE}/${record.id}/edit`}>
                                {text}
                            </Link>
                        )
                    },
                    sorter: true,
                    ellipsis: true,
                    width: COLUMN_NAME_WIDTH,
                    fixed: 'left'
                },
                {
                    title: 'Ad Groups',
                    dataIndex: 'adGroups',
                    render: (value, record) => (
                        <Link to={`${CommonRoutes.AD_GROUP}?creativeId=${record.id}`} style={{color: '#5888CF'}}>
                            {value}
                        </Link>
                    ),
                    width: 70
                },
                ...(agency
                    ? [
                        {
                            title: 'Advertiser',
                            dataIndex: 'advertiserName',
                            sorter: true,
                            ellipsis: true,
                            width: 120
                        }
                    ]
                    : []),
                {
                    title: 'Status',
                    dataIndex: 'status',
                    sorter: true,
                    width: 80,
                    render: (_, record: CreativeBean) => (
                        <Tag color={record.status ? statusColors[record.status] : '#D6E6FF'} style={{color: '#0D1A43', textAlign: 'center'}}>
                            {record.status}
                        </Tag>
                    )
                },
                {
                    title: 'Format',
                    dataIndex: 'format',
                    sorter: true,
                    width: 120,
                    render: (_, record: CreativeListItemBean) => record.format && formatNames[record.format]
                },
                {
                    title: 'Size',
                    dataIndex: 'width',
                    width: 120,
                    sorter: true,
                    render: (_, record: CreativeListItemBean) =>
                        record.width && record.width > 1 && record.height && record.height >= 1 ?
                            `${record.width}x${record.height}` : '-'
                },
                {
                    title: 'Last Modified',
                    dataIndex: 'modified',
                    sorter: true,
                    width: 100,
                    render: (date) => date && dayjs(date).format('DD MMM YY')
                },
                {
                    title: 'Active',
                    dataIndex: 'active',
                    sorter: true,
                    width: 76,
                    render: (_, record: CreativeBean) => (
                        <Switch
                            size="small"
                            checked={record.active}
                            onChange={(checked) => record.id && toggleActive(record, checked)}
                        />
                    )
                },
                {
                    title: 'Actions',
                    render: (_, record: CreativeBean) => (
                        <div style={{display: 'flex', gap: 8, justifyContent: 'center'}}>
                            <ListActionButton onClick={() => navigateTo(`${CommonRoutes.CREATIVE}/${record.id}/copy`)}
                                    tooltip="Duplicate">
                                <CopyOutlined/>
                            </ListActionButton>
                            <GoToReportButton title="Report" filterName="CreativeId" id={record.id}/>
                        </div>
                    ),
                    width: 100
                }
            ]}
        >
            <SearchInput value={filters.name} onChange={(value) => setFilter('name', value)}/>
            {agency && (
                <AdvertiserSelector
                    placeholder="Filter by Advertiser"
                    style={{minWidth: '200px'}}
                    value={filters.advertiserId}
                    onChange={(value: number) => setFilter('advertiserId', value)}
                />
            )}
            <AdGroupSelector
                placeholder="Filter by Ad Group"
                style={{minWidth: '200px'}}
                value={filters.adGroupId}
                onChange={(value: number) => setFilter('adGroupId', value)}
                advertiserId={filters.advertiserId}
            />
            <CreativeStatusSelector
                placeholder="Filter by Status"
                style={{minWidth: '100px'}}
                value={filters.status}
                onChange={(value) => setFilter('status', value)}
            />
            <ActiveStatusSelector
                placeholder="Filter by Active"
                style={{minWidth: '100px'}}
                value={filters.active}
                onChange={(value) => setFilter('active', value)}
            />
        </ListCard>
    )
}

export default CreativeList
